/**
 * TwilioSinkAdminGuard — wraps any settings panel that depends on the
 * Twilio sink-admin's personal preferences.
 *
 * For Twilio extensions, only ONE user-id is the "integration admin" for
 * a given (orgId, appCode) pair (see TWILIO_ACCOUNT_SINK_MAPS Firestore
 * collection). That user's preferences (assignment rules, ignored numbers,
 * forwarding webhooks, etc.) are what the webhook fan-out CF applies to
 * incoming events — every other user's edits write to the wrong personal
 * doc and are silently ignored.
 *
 * Two variants:
 *   • `default` (settings panels) — renders the integration-admin card on
 *     top, then the children with `space-y-6`. Mismatch dims the children
 *     and shows a centered hint card explaining who manages them.
 *   • `fullscreen` (builder pages, field-mapping) — preserves the
 *     children's flex/h-full layout, omits the admin card (would conflict
 *     with custom headers), and overlays the entire content area when
 *     mismatched. The hint card directs the user to the Settings page to
 *     switch.
 *
 * For non-Twilio TPAs and shared-access users it's a no-op pass-through
 * for both variants.
 */
import { useState, type ReactNode } from 'react';
import { ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { useModuleStore } from '@/stores/module-store';
import { TwilioSinkAdminCard, type SinkAdminInfo } from './incoming-sync/tpa/twilio-sink-admin-card';

export function TwilioSinkAdminGuard({
  children,
  variant = 'default',
}: {
  children: ReactNode;
  /** `default` adds the admin card + space-y-6 layout. `fullscreen` keeps
   * the children's own layout and only renders the dimming overlay. */
  variant?: 'default' | 'fullscreen';
}) {
  const appCodeTPA = useAppStore((s) => s.appCodeTPA);
  const extensionName = useAppStore((s) => s.extensionName);
  const orgId = useAppStore((s) => s.appConfig?.orgId);
  const tpaAuthenticated = useModuleStore((s) => s.tpaAuthenticated);
  const isSharedUser = useAuthStore((s) => !!s.selectedAdminUserId);
  const currentUserUid = useAuthStore((s) => s.user?.uid ?? null);

  const [sinkAdmin, setSinkAdmin] = useState<SinkAdminInfo | null>(null);

  const showSinkAdmin =
    appCodeTPA === 'twilio' &&
    !isSharedUser &&
    tpaAuthenticated &&
    !!extensionName &&
    !!orgId;

  const isAdminMismatch =
    showSinkAdmin && !!sinkAdmin?.uid && !!currentUserUid && sinkAdmin.uid !== currentUserUid;

  // Pass-through for non-Twilio TPAs, shared users, or unauthenticated state.
  if (!showSinkAdmin) return <>{children}</>;

  // Fullscreen variant — for builders / pages with their own layout.
  // We still need to mount the admin card (it owns the fetch + state) but
  // hide it visually so the parent's chrome stays intact.
  if (variant === 'fullscreen') {
    return (
      <div className="relative flex flex-col h-full">
        {/* Mount the card off-screen so the same fetch/store hooks fire */}
        <div className="hidden">
          <TwilioSinkAdminCard
            extensionName={extensionName!}
            orgId={orgId!}
            onCurrentAdminChange={setSinkAdmin}
          />
        </div>
        <div
          className={cn(
            'flex flex-col h-full min-h-0',
            isAdminMismatch && 'pointer-events-none opacity-50 select-none',
          )}
        >
          {children}
        </div>
        {isAdminMismatch && (
          <div className="absolute inset-0 z-50 flex items-center justify-center px-6 pointer-events-auto">
            <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px]" />
            <div className="relative rounded-lg border border-border bg-background shadow-lg p-5 max-w-md text-center">
              <ShieldCheck className="h-6 w-6 text-primary mx-auto mb-2" />
              <p className="text-sm font-medium">
                Managed by{' '}
                <span className="text-primary">
                  {sinkAdmin?.name?.trim() || sinkAdmin?.email || 'another team member'}
                </span>
              </p>
              {sinkAdmin?.email && sinkAdmin.name?.trim() && sinkAdmin.name !== sinkAdmin.email && (
                <p className="text-[11px] text-muted-foreground mt-0.5">{sinkAdmin.email}</p>
              )}
              <p className="text-xs text-muted-foreground mt-3">
                Set yourself as the integration admin from the
                {' '}
                <span className="font-medium text-foreground">Settings → Incoming Sync</span>
                {' '}
                page to make changes here.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default variant — settings panels with admin card + dimmed children.
  return (
    <div className="space-y-6">
      <TwilioSinkAdminCard
        extensionName={extensionName!}
        orgId={orgId!}
        onCurrentAdminChange={setSinkAdmin}
      />

      <div className="relative">
        {isAdminMismatch && (
          <div className="absolute inset-0 z-20 flex items-start justify-center pt-10 px-6 rounded-lg pointer-events-auto">
            <div className="absolute inset-0 rounded-lg bg-background/40 backdrop-blur-[1px]" />
            <div className="relative rounded-lg border border-border bg-background shadow-lg p-4 max-w-md text-center">
              <ShieldCheck className="h-5 w-5 text-primary mx-auto mb-2" />
              <p className="text-sm font-medium">
                These settings are managed by{' '}
                <span className="text-primary">
                  {sinkAdmin?.name?.trim() || sinkAdmin?.email || 'another team member'}
                </span>
              </p>
              {sinkAdmin?.email && sinkAdmin.name?.trim() && sinkAdmin.name !== sinkAdmin.email && (
                <p className="text-[11px] text-muted-foreground mt-0.5">{sinkAdmin.email}</p>
              )}
              <p className="text-xs text-muted-foreground mt-2">
                Set yourself as the integration admin above to edit assignment rules,
                phone-matching, message recording, forwarding webhooks, and other inbox
                preferences for this organization.
              </p>
            </div>
          </div>
        )}
        <div
          className={cn(
            'space-y-6',
            isAdminMismatch && 'pointer-events-none opacity-50 select-none',
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
